import { PROJECT_COLORS } from '@vd/tokens';
import styles from './ProjectColorPicker.module.css';

/**
 * Project color picker — twelve preset swatches plus a native color input
 * for users who want a specific brand color outside the palette. Used by
 * the CreateProjectModal and the General tab on the project settings page.
 *
 * Selection styling: only the active swatch carries the "on" indicator
 * (a thicker text-color outline). Unselected swatches are bare so the
 * eye lands on the picked one without visual noise.
 */
export interface ProjectColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  /** What the swatches are the colour of, for a screen reader. */
  label?: string;
  /** The palette only, with no custom colour: a workspace's must be one (TOADO-906). */
  paletteOnly?: boolean;
  disabled?: boolean;
}

export function ProjectColorPicker({ value, onChange, label = 'Project color', paletteOnly = false, disabled = false }: ProjectColorPickerProps) {
  const isCustom = !PROJECT_COLORS.some((c) => c.toLowerCase() === value.toLowerCase());

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        role="radiogroup"
        aria-label={label}
        style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
      >
        {PROJECT_COLORS.map((c) => {
          const selected = c.toLowerCase() === value.toLowerCase();
          return (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={`Color ${c}`}
              disabled={disabled}
              onClick={() => onChange(c)}
              style={{
                width: 22,
                height: 22,
                borderRadius: 6,
                background: c,
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                outline: selected ? '2px solid var(--text)' : 'none',
                outlineOffset: selected ? 2 : 0,
              }}
            />
          );
        })}
      </div>

      {!paletteOnly && <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label="Custom project color"
          className={styles.custom}
          style={{ border: isCustom ? '2px solid var(--text)' : '1px solid var(--border)' }}
        />
        Custom color
      </label>}
    </div>
  );
}
